import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NewUnit } from 'app/models/Unit/CreateUnit';
import { OrderFacilityDetails } from 'app/models/Facility/FacilityDetails';
import { UnitResponse } from 'app/models/Facility/Unit';
import {
  OrderHeaderSection,
  IAtAGlance,
  QuickGlance,
  OrderResponse,
  NewOrder,
  ITypeAheadOption,
  IOrderReportDetails,
} from 'app/models/Orders/OrderDetails';
import { IOrderDetails } from 'app/models/Orders/OrderDetails';
import { FacilityResponse } from 'app/models/Facility/FacilityDetails';
import { serviceActions } from './async-actions';
import { IOrderPackagesInteraction, IOrderPayPackages, IOrderRateElement, IPlacementPayPackageRow } from './types';
import { GridColumns } from '@mui/x-data-grid-pro';
import { IFacilityDetails } from '@AMIEWEB/Facility/FacilityStore/Facility.model';
import { ISelectedSubCert } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Grid/FacilityOrderUnitGrid';

export type LevelTypes = 'Standardize' | 'Classic' | 'None';

export interface LevelRules {
  fieldName: string;
  isRequired: boolean;
  disabled: boolean;
  level: LevelTypes;
}

export const OrderSkillsetUpdateError = 60119;

export interface OrdersPage {
  orders: OrderResponse[] | null;
  totalCount: number;
  pageSize: number;
}

export interface IRateElementShiftOcbr {
  shiftId: number;
  shiftValue: string;
  shiftDisplayValue: string;
}

export enum Qualification {
  license = 'license',
  certification = 'certification',
}

export enum CompactStatus {
  single = 'Single State',
  compact = 'Compact',
}

export interface ILicenseStateData {
  state?: any[];
  isRequired?: boolean;
  documentAttachmentRequired?: boolean;
}

export interface ILicenseErrorDetails {
  compactStatus?: boolean;
  state?: boolean;
}

export interface IOrderLicense {
  licenseDescription?: string;
  licenseId?: number;
  licenseAbbreviation?: string;
  compactStatus?: CompactStatus;
  abbreviation?: string;
  licenseStateDetails?: ILicenseStateData[];
  isError?: ILicenseErrorDetails;
  isSelected?: boolean;
}

export interface IOrderCertification {
  abbreviation?: string;
  isRequired?: boolean;
  documentAttachmentRequired?: boolean;
  requirementTypeId?: number;
  isError?: boolean;
  isSubCert?: boolean;
}

export interface IOrderLicenseState {
  state: any;
  isRequired?: boolean;
  documentAttachmentRequired?: boolean;
}

export interface IOrderBonusTab {
  orderBonusList: any[];
  orderBonusShiftList: any[];
  OrderBonusCalculatedAmount: { type: string; rate: number; returnValue: number; error: string };
  isOrderBonusCreated: any;
  isOrderBonusDeleted: boolean;
  isOrderBonusCopied: boolean;
  editBonusModal: { isEdit: boolean; bonusId: number; rowData: any };
  changeHistoryModal: { open: boolean; changeHistoryRows: any };
  updateOrderBonusShift: boolean;
}

export const initialOrderBonusTab: IOrderBonusTab = {
  orderBonusList: [],
  orderBonusShiftList: [],
  OrderBonusCalculatedAmount: null,
  isOrderBonusCreated: false,
  isOrderBonusDeleted: false,
  isOrderBonusCopied: false,
  editBonusModal: { isEdit: false, bonusId: 0, rowData: null },
  changeHistoryModal: { open: false, changeHistoryRows: null },
  updateOrderBonusShift: false,
};
export interface IOrdersDetails {
  details: any;
  newOrder: NewOrder | null;
  newUnit: NewUnit | null;
  level: LevelTypes;
  levelRules: LevelRules[] | null;
  orders: OrdersPage | null;
  loading: boolean;
  isEditUnit: boolean;
  currentUnitId: any | null;
  activeStepCount: any;
  reload: boolean;
  orderCreationProcessStep: IOrderCreationProcessStep | null;
  editUnitProcessStep: IUnitEditProcessStep | null;
  orderDetailsView: IOrderDetailsView | null;
  orderUnitDetails: Number;
  isOrderFormValid: boolean | false;
  typeAheadOptions: ITypeAheadOptions | null;
  overrideCreditHold: boolean | false;
  orderStrikeDetails: IStrikeDetailsRequest | null;
  isStrikeDataUpdated: boolean;
  rateElements: IOrderRateElement[];
  rateCardView: {
    rates: any[];
    rateColumns: GridColumns[];
    skillsets: any[];
    skillsetColumns: GridColumns[];
  };
  orderBonuses: IOrderBonus[];
  orderLicenses: IOrderLicense[];
  orderCertification: IOrderCertification[];
  subCertificationList: any[];
  orderTypes: Array<{
    ID: number;
    Description: string;
  }>;
  regions: { ID: number; Description: string }[];
  isPreferenceChanged: boolean;
  confirmationDialog: IConfirmationDialog;
  tabValue: number;
  orderBonusTab: IOrderBonusTab;
  selectedSubCert: ISelectedSubCert[];
  orderChangeHistory: any[];
  targetMargins: IOrderTargetMargin[];
  targetMarginOriginalResponse: ITargetMarginOriginalResponse | null;
  tmVersion: number; // Versioning for target margins to trigger re-renders
  weeklyGrossPayDetails: any[];
  orderPayPackages: IOrderPayPackages[];
  orderPayPackagesSource: IOrderPayPackages[];
  placementPayPackages: IPlacementPayPackageRow[];
  placementPayPackagesSource: IPlacementPayPackageRow[];
  placementPayPackagesLoaded: boolean;
  placementPayPackagesPagination: {
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
  };
  orderPayPackagesGridIsDirty: boolean;
  orderPayPackagesGridActiveEditRow: any;
  weeklyGrossPayFetchError: boolean;
  orderPayPackagesInteraction: IOrderPackagesInteraction;
  gridDirtyRows: number[];
  isMarginOrderBaseDetailsValid: boolean;
  targetMarginResetResposne: ITargetMarginOriginalResponse | null;
}
export interface ITargetMarginOriginalResponse {
  isRefreshFlow: boolean | false;
  targetMarginOrders: IOrderTargetMargin[];
  targetMarginOrderUpdateHistoryItems: any[];
}

export interface IConfirmationDialog {
  open: boolean;
  tab: number;
}

export const initialState: IOrdersDetails = {
  details: null,
  newOrder: null,
  newUnit: null,
  level: 'None',
  levelRules: null,
  orders: null,
  loading: false,
  isEditUnit: false,
  currentUnitId: 0,
  activeStepCount: 0,
  reload: false,
  orderCreationProcessStep: null,
  editUnitProcessStep: null,
  orderDetailsView: null,
  orderUnitDetails: 0,
  isOrderFormValid: false,
  typeAheadOptions: null,
  overrideCreditHold: false,
  orderStrikeDetails: null,
  isStrikeDataUpdated: false,
  rateElements: [],
  rateCardView: {
    rates: [],
    rateColumns: [],
    skillsets: [],
    skillsetColumns: [],
  },
  orderBonuses: [],
  orderLicenses: [],
  orderCertification: [],
  subCertificationList: [],
  orderTypes: [],
  regions: [],
  isPreferenceChanged: false,
  confirmationDialog: { open: false, tab: 0 },
  tabValue: 0,
  orderBonusTab: initialOrderBonusTab,
  selectedSubCert: [],
  orderChangeHistory: [],
  targetMargins: [],
  tmVersion: 0, // Versioning for target margins to trigger re-renders
  weeklyGrossPayDetails: [],
  orderPayPackages: [], //stores the data to be displayed in grid
  orderPayPackagesSource: [], //stores the original data fetched from API
  placementPayPackages: [], //stores the placement pay packages to be displayed in grid
  placementPayPackagesSource: [], //stores the original placement pay packages fetched from API
  placementPayPackagesLoaded: false,
  placementPayPackagesPagination: {
    totalCount: 0,
    pageNumber: 1,
    pageSize: 20,
    totalPages: 0,
  },
  orderPayPackagesGridIsDirty: false, //flag to track if any changes done in grid
  orderPayPackagesGridActiveEditRow: null, //stores the row which is in edit mode
  weeklyGrossPayFetchError: false,
  targetMarginOriginalResponse: null,
  orderPayPackagesInteraction: { action: null },
  gridDirtyRows: [],
  isMarginOrderBaseDetailsValid: false,
  targetMarginResetResposne: null, //stores the response of reset target margin API
};

export interface IOrderCreationProcessStep {
  facilityId: number;
  facilityData: OrderFacilityDetails;
  selectedNewUnitId: number | -1;
  selectedNewUnit: NewUnit | null;
  unitsData: UnitResponse[] | null;
  newOrder: NewOrder | null;
  isMandatoryFieldsFilled: boolean | false;
  isMandatorySecondaryFieldsFilled: boolean | false;
  showMandetryStyle: boolean | false;
  isUnitSelected: boolean | false;
  isRequirementEdited: boolean | false;
  isDetailsUpdated: boolean | false;
  editMode: string | 'standardize';
  currentStep: string;
  requestedBack: boolean | false;
  activeStep: number | 0;
  isEditStarted: boolean | false;
}

export interface IUnitEditProcessStep {
  facilityId: number;
  facilityResponse: FacilityResponse | null;
  facilityDataForUnit: any | null;
  selectedNewUnitId: number | -1;
  selectedNewUnit: NewUnit | null;
  unitsData: UnitResponse[] | null;
  selectedUnitData: UnitResponse | null;
  readyToEdit: boolean | false;
  editStarted: boolean | false;
  isRememebrCheckboxSelected: boolean | false;
  editMode: string;
  callingComponent?: string;
}

export interface ITypeAheadOptions {
  disciplines: ITypeAheadOption[];
  specialties: ITypeAheadOption[];
  subSpecialties: ITypeAheadOption[];
  divisionDisciplines?: any[];
}

export interface IOrderDetailsView {
  orderResponse: OrderResponse;
  orderDetails: IOrderDetails;
  orderDetailsOrginal: IOrderDetails;
  facilityId: number;
  headerDetails: OrderHeaderSection;
  atAGlance: IAtAGlance;
  quickGlanceDetails: QuickGlance;
  facilityDetails: IFacilityDetails;
  selectedNewUnitId: number | -1;
  selectedNewUnit: NewUnit | null;
  unitsData: UnitResponse[] | null;
  isMandatoryFieldsFilled: boolean | false;
  isEditStarted: boolean | false;
  editMode: string | 'inline';
  currentStep: string;
  newOrder: NewOrder | null;
  creditStatus: boolean | false;
  pendingStatus: boolean | false;
  orderReportDetails: IOrderReportDetails | null;
}

export interface IStrikeDetailsRequest {
  facilityId: number;
  orderId: number;
  oKtoTravel: boolean;
  orderStrikeNoticeId: number;
  strikeNoticeDate: string;
  effectiveDate: string;
  endDate: string;
  travelDate1: string;
  travelDate2: string;
  travelDate3: string;
  travelDate4: string;
  travelDate5: string;
  lastUpdatedbyId: number;
}

export interface IOrderBonus {
  division: string;
  clientType: string;
  mspClient: string;
  facilityId: number | string;
  facilityStatusId: number | string;
  facilityStatus: string;
  fullDescFacilityStatus: string;
  facilityName: string;
  unitName: string;
  orderId: number | string;
  orderPriority: string;
  skillSets: string;
  shifts: string;
  orderBillRate: number | string | null;
  maxFilesSent: string;
  positionsAvailable: number | string;
  pricingStatus: string;
  bonusAmount: number | string;
  bonusStartDate: Date | string;
  bonusEndDate: Date | string;
  bonusId: number | string;
  statusId: number | string;
  bonusStatus: string;
  bonusType: string;
  orderCreationDate: Date | string;
  statusChangedDate: Date | string;
  statusChangedByUserId: number | string;
  changedByFirstName: string;
  changedByLastName: string;
  totalCount: number;
}

export interface IOrderBonusCommand {
  pageNumber: number;
  statusIds: number[];
  bonusTypeIds: number[];
  facilityIds: number[];
  facilityStatusIds: number[];
  orderIds: number[];
  mspClientIds: number[];
  orderBillRate: any;
  maxFilesSentId: number;
  positions: any;
  sortColumn: string;
  sortOrder: string;
  pageSize?: number;
  divisionIds: number[];
  disciplineIds: number[];
  specialtyIds: number[];
  subSpecialtyIds: number[];
  orderShiftIds: number[];
  orderPriorityIds: number[];
}

export interface IOrderBonusSave {
  orderId: number;
  bonusId: number;
  statusId: number;
  bonusAmount: number;
  bonusStartDate: string;
  bonusEndDate: string;
  totalCount: number;
  paymentTypeId: number;
  bonusTypeId: number;
  payerId: number;
  billingTypeId: number;
  billingAmount: number;
  approvedBy: string;
  approvalTypeID: number;
  approvalDate: string;
  startDate: string;
  endDate: string;
  bonusShortHoursTypeId: number;
  shortAssignmentBonusId: number;
  notes: string;
  minimumWeeksWorked: number;
  bonusBillingAmount: number;
  bonusTemplateId: number;
  userId: number;
  shiftIds?: number[];
  timeStamp?: string;
  deletedOrderBonusShifts?: number[];
}

export interface IOrderTargetMargin {
  rowId?: number;
  id: number;
  orderId: number;
  disciplineId: number;
  discipline: string;
  specialtyId: number;
  specialty: string;
  subSpecialtyId?: number;
  subSpecialty: string;
  targetMarginSourceId: number;
  targetMarginSource: string;
  entityId: number;
  entityName: string;
  targetMarginDefault?: number;
  targetMarginDayShift?: number;
  targetMarginNightShift?: number;
  targetMarginDayNightShift?: number;
  payRangeMinMaxPercentage?: number;
  defaultWeeklyPay?: number;
  isActive: boolean;
  createdUserId: number;
  createdFirstName: string;
  createdLastName: string;
  createdDate: string; // ISO date string
  lastUpdatedUserId: number;
  lastUpdatedFirstName: string;
  lastUpdatedLastName: string;
  lastUpdatedDate: string; // ISO date string
  publishingStatus: string;
  isDetailsModified: boolean;
  updateCustomPayPackage?: boolean;
  isCustom?: boolean;
  relatedSourceEntityId?: number | null;
  mode?: number;
  isModified?: boolean;
  isNewCombo?: boolean;
}

export interface IUpdateOrderTargetMargin {
  orderId: number;
  userId: number;
  targetMarginOrders: IOrderTargetMargin[];
}
const orderDataSlice = createSlice({
  name: 'orderData',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        loading: action.payload,
      };
    },
    setDetails(state, action: PayloadAction<any>) {
      state.details = action.payload;
    },
    setNewOrder(state, action: PayloadAction<NewOrder | null>) {
      state.newOrder = action.payload;
    },
    setNewUnit(state, action: PayloadAction<NewUnit | null>) {
      state.newUnit = action.payload;
    },
    setIsEditUnit(state, action: PayloadAction<boolean>) {
      state.isEditUnit = action.payload;
    },
    setLevel(state, action: PayloadAction<LevelTypes>) {
      state.level = action.payload;
    },
    setLevelRules(state, action: PayloadAction<LevelRules[]>) {
      state.levelRules = action.payload;
    },
    setOrders(state, action: PayloadAction<OrdersPage>) {
      state.orders = action.payload;
    },
    setCurrentUnitId(state, action: PayloadAction<any>) {
      state.currentUnitId = action.payload;
    },
    setActiveStepCount(state, action: PayloadAction<any>) {
      state.activeStepCount = action.payload;
    },
    setReload(state, action: PayloadAction<boolean>) {
      state.reload = action.payload;
    },
    setOrderCreationProcessStep(state, action: PayloadAction<IOrderCreationProcessStep | null>) {
      state.orderCreationProcessStep = action.payload;
    },
    setDetailsCurrentStep(state, action: PayloadAction<string>) {
      if (state.orderDetailsView) state.orderDetailsView.currentStep = action.payload;
    },
    setUnitEditProcessStep(state, action: PayloadAction<IUnitEditProcessStep | null>) {
      state.editUnitProcessStep = action.payload;
    },
    setOrderDetailsView(state, action: PayloadAction<IOrderDetailsView | null>) {
      state.orderDetailsView = action.payload;
    },
    setFormValid(state, action: PayloadAction<boolean>) {
      state.isOrderFormValid = action.payload;
    },
    setTypeAheadOptions(state, action: PayloadAction<ITypeAheadOptions>) {
      state.typeAheadOptions = action.payload;
    },
    setOverrideCreditHold(state, action: PayloadAction<boolean>) {
      state.overrideCreditHold = action.payload;
    },
    setOrderStrikeDetails(state, action: PayloadAction<IStrikeDetailsRequest | null>) {
      state.orderStrikeDetails = action.payload;
    },
    setStrikeUpdateStatus(state, action: PayloadAction<boolean>) {
      state.isStrikeDataUpdated = action.payload;
    },
    setOrderLicenseDetails(state, action: PayloadAction<IOrderLicense[]>) {
      state.orderLicenses = action.payload;
    },
    setsubCertificationList(state, action: PayloadAction<any[]>) {
      state.subCertificationList = action.payload;
    },
    setOrderCertificationDetails(state, action: PayloadAction<IOrderCertification[]>) {
      state.orderCertification = action.payload;
    },
    /**Code Refactor - begin */
    setOrderRateElements(
      state,
      action: PayloadAction<{
        rateElements: IOrderRateElement[];
        rates: any[];
        rateColumns: GridColumns[];
        skillsets: any[];
        skillsetColumns: GridColumns[];
      }>,
    ) {
      const rateCardView = {
        rates: action.payload.rates,
        rateColumns: action.payload.rateColumns,
        skillsets: action.payload.skillsets,
        skillsetColumns: action.payload.skillsetColumns,
      };
      return { ...state, rateElements: action.payload.rateElements, rateCardView };
    },
    resetRateElements(state) {
      return { ...state, rateElements: [], rateCardView: initialState.rateCardView };
    },
    setOrderBonuses(state, action: PayloadAction<IOrderBonus[]>) {
      state.orderBonuses = action.payload;
    },
    setOrderTypes(state, action: PayloadAction<Array<{ ID: number; Description: string }>>) {
      state.orderTypes = action.payload;
    },
    setRegions(state, action: PayloadAction<{ ID: number; Description: string }[]>) {
      state.regions = action.payload;
    },
    setIsPreferenceChaged(state, action: PayloadAction<boolean>) {
      state.isPreferenceChanged = action.payload;
    },
    setConfirmationDialog(state, action: PayloadAction<IConfirmationDialog>) {
      state.confirmationDialog = action.payload;
    },
    setTabValue(state, action: PayloadAction<number>) {
      state.tabValue = action.payload;
    },
    setOrderBonusList(state, action: PayloadAction<any[]>) {
      state.orderBonusTab.orderBonusList = action.payload;
    },
    setOrderBonusShifts(state, action: PayloadAction<any[]>) {
      state.orderBonusTab.orderBonusShiftList = action.payload;
    },
    SetOrderUnitDetails(state, action: PayloadAction<number>) {
      state.orderUnitDetails = action.payload;
    },
    setUpdateOrderBonusShifts(state, action: PayloadAction<boolean>) {
      state.orderBonusTab.updateOrderBonusShift = action.payload;
    },
    setOrderBonusCalculatedAmount(
      state,
      action: PayloadAction<{ type: string; rate: number; returnValue: number; error: string }>,
    ) {
      state.orderBonusTab.OrderBonusCalculatedAmount = action.payload;
    },
    setEditOrderBonusModal(state, action: PayloadAction<{ isEdit: boolean; bonusId: number; rowData: any }>) {
      state.orderBonusTab.editBonusModal = action.payload;
    },
    setOrderBonusCreated(state, action: PayloadAction<boolean>) {
      state.orderBonusTab.isOrderBonusCreated = action.payload;
    },
    setCopyOrderBonus(state, action: PayloadAction<boolean>) {
      state.orderBonusTab.isOrderBonusCopied = action.payload;
    },
    setDeleteOrderBonus(state, action: PayloadAction<boolean>) {
      state.orderBonusTab.isOrderBonusDeleted = action.payload;
    },
    setOrderBonusChangeHistory(
      state,
      action: PayloadAction<{
        open: boolean;
        changeHistoryRows: any;
      }>,
    ) {
      state.orderBonusTab.changeHistoryModal = action.payload;
    },
    setSelectedSubCert(state, action) {
      state.selectedSubCert = action.payload;
    },
    setOrderChangeHistory(state, action: PayloadAction<any>) {
      state.orderChangeHistory = action.payload;
    },
    setOrderTargetMargins(state, action: PayloadAction<IOrderTargetMargin[]>) {
      state.targetMargins = action.payload.map(val => ({ ...val })); //create new ref of array so that it can be tracked by useEffect
      state.tmVersion = (state.tmVersion ?? 0) + 1;
    },
    setMarginOrderBaseDetailsValidStatus(state, action: PayloadAction<boolean>) {
      state.isMarginOrderBaseDetailsValid = action.payload;
    },
    setTargetMarginOriginalResposne(state, action: PayloadAction<ITargetMarginOriginalResponse>) {
      state.targetMarginOriginalResponse = action.payload;
    },
    setTargetMarginResetResposne(state, action: PayloadAction<ITargetMarginOriginalResponse>) {
      state.targetMarginResetResposne = action.payload;
    },
    resetOrderTargetMargin(state) {
      state.targetMargins = [];
      state.tmVersion = 0;
    },
    setWeeklyGrossPayDetails(state, action: PayloadAction<any[]>) {
      state.weeklyGrossPayDetails = action.payload;
    },
    setOrderPayPackages(state, action: PayloadAction<IOrderPayPackages[]>) {
      state.orderPayPackages = action.payload;
    },
    setOrderPayPackagesSource(state, action: PayloadAction<IOrderPayPackages[]>) {
      state.orderPayPackagesSource = action.payload;
    },
    setPlacementPayPackages(state, action: PayloadAction<IPlacementPayPackageRow[]>) {
      state.placementPayPackages = action.payload;
      state.placementPayPackagesLoaded = true;
    },
    setPlacementPayPackagesPagination(
      state,
      action: PayloadAction<{ totalCount: number; pageNumber: number; pageSize: number; totalPages: number }>,
    ) {
      state.placementPayPackagesPagination = action.payload;
    },
    setPlacementPayPackagesSource(state, action: PayloadAction<IPlacementPayPackageRow[]>) {
      state.placementPayPackagesSource = action.payload;
    },
    setPlacementPayPackagesLoaded(state, action: PayloadAction<boolean>) {
      state.placementPayPackagesLoaded = action.payload;
    },
    setOrderPayPackagesInteraction(state, action: PayloadAction<IOrderPackagesInteraction>) {
      state.orderPayPackagesInteraction = action.payload;
    },
    setOrderPayPackagesGridIsDirty(state, action: PayloadAction<boolean>) {
      state.orderPayPackagesGridIsDirty = action.payload;
    },
    setOrderPayPackagesGridActiveEditRow(state, action: PayloadAction<any>) {
      state.orderPayPackagesGridActiveEditRow = action.payload;
    },
    setWeeklyGrossPayFetchError(state, action: PayloadAction<boolean>) {
      state.weeklyGrossPayFetchError = action.payload;
    },
    setDirtyRows(state, action: PayloadAction<number[] | number>) {
      if (typeof action.payload === 'number') {
        if (!state.gridDirtyRows.includes(action.payload)) {
          state.gridDirtyRows.push(action.payload);
        }
      } else {
        state.gridDirtyRows = action.payload;
      }
    },
    reset() {
      return {
        ...initialState,
      };
    },
  },
});

export const { reducer: orderDataReducer, name: orderDataSliceKey } = orderDataSlice;

export const orderDataActions = {
  ...orderDataSlice.actions,
  ...serviceActions,
};
